import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface OrganizationSettings {
  id: string;
  name: string;
  slug: string;
  plan: string;
  max_videos: number;
  max_storage_gb: number;
  video_count: number;
  storage_used_gb: number;
}

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<'general' | 'users' | 'integrations' | 'ai-access' | 'billing'>('general');
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Handle YouTube OAuth callback params
  useEffect(() => {
    const youtubeConnected = searchParams.get('youtube_connected');
    const youtubeError = searchParams.get('youtube_error');

    if (youtubeConnected || youtubeError) {
      // Switch to integrations tab
      setActiveTab('integrations');
      // Clear the params from URL
      setSearchParams({});
      // Invalidate YouTube status query
      queryClient.invalidateQueries({ queryKey: ['youtube-status'] });
    }
  }, [searchParams, setSearchParams, queryClient]);

  const { data: org, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: () => api.get<{ data: OrganizationSettings }>('/admin/organization'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const tabs = [
    { id: 'general', name: 'General' },
    { id: 'users', name: 'Users' },
    { id: 'integrations', name: 'Integrations' },
    { id: 'ai-access', name: 'AI Access' },
    { id: 'billing', name: 'Billing' },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your organization settings
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'general' && <GeneralSettings org={org?.data} />}
      {activeTab === 'users' && <UsersSettings />}
      {activeTab === 'integrations' && <IntegrationsSettings />}
      {activeTab === 'ai-access' && <AiAccessSettings />}
      {activeTab === 'billing' && <BillingSettings org={org?.data} />}
    </div>
  );
}

function GeneralSettings({ org }: { org?: OrganizationSettings }) {
  const [name, setName] = useState(org?.name || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: { name: string }) => api.patch('/admin/organization', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['organization'] }),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await mutation.mutateAsync({ name });
    setIsSubmitting(false);
  };

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Organization Details</h3>
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div>
            <label className="label">Organization Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label className="label">Slug</label>
            <input
              type="text"
              value={org?.slug || ''}
              className="input bg-gray-50"
              disabled
            />
            <p className="text-xs text-gray-500 mt-1">
              Used in your subdomain: {org?.slug}.searchmycontent.com
            </p>
          </div>

          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm text-gray-500">Videos</p>
            <p className="text-2xl font-semibold">
              {org?.video_count} / {org?.max_videos}
            </p>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-600 rounded-full"
                style={{ width: `${((org?.video_count || 0) / (org?.max_videos || 1)) * 100}%` }}
              />
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500">Storage</p>
            <p className="text-2xl font-semibold">
              {org?.storage_used_gb?.toFixed(2) || 0} GB / {org?.max_storage_gb} GB
            </p>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-600 rounded-full"
                style={{ width: `${((org?.storage_used_gb || 0) / (org?.max_storage_gb || 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersSettings() {
  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ data: Array<{ id: string; email: string; name: string; role: string; last_login: string }> }>('/admin/users'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
        <button className="btn-primary">Invite User</button>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data?.data?.map((user) => (
            <tr key={user.id}>
              <td className="px-6 py-4">
                <div>
                  <p className="font-medium text-gray-900">{user.name || user.email}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 capitalize">
                  {user.role}
                </span>
              </td>
              <td className="px-6 py-4 text-sm text-gray-500">
                {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
              </td>
              <td className="px-6 py-4 text-right">
                <button className="text-primary-600 hover:text-primary-900 text-sm font-medium">
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface YouTubeStatus {
  connected: boolean;
  channel?: {
    id: string;
    name: string;
    thumbnail_url: string;
    video_count: number;
  };
  connected_at?: string;
  token_expired?: boolean;
}

function IntegrationsSettings() {
  const queryClient = useQueryClient();
  const [isConnecting, setIsConnecting] = useState(false);

  const { data: youtubeStatus, isLoading } = useQuery({
    queryKey: ['youtube-status'],
    queryFn: () => api.get<{ data: YouTubeStatus }>('/integrations/youtube/status'),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => api.delete('/integrations/youtube'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['youtube-status'] });
    },
  });

  const handleConnectYouTube = async () => {
    setIsConnecting(true);
    try {
      const response = await api.get<{ data: { authorization_url: string } }>('/integrations/youtube/auth');
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to start YouTube auth:', error);
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    if (confirm('Are you sure you want to disconnect your YouTube channel? You will need to reconnect to import videos with captions.')) {
      disconnectMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  const status = youtubeStatus?.data;

  return (
    <div className="space-y-6">
      {/* YouTube Integration */}
      <div className="card p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="h-6 w-6 text-red-600" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">YouTube</h3>
            <p className="text-sm text-gray-500 mt-1">
              Connect your YouTube channel to import videos with captions directly. This allows you to use existing captions without external transcription services.
            </p>

            {status?.connected ? (
              <div className="mt-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  {status.channel?.thumbnail_url && (
                    <img
                      src={status.channel.thumbnail_url}
                      alt={status.channel.name}
                      className="h-10 w-10 rounded-full"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-green-900">{status.channel?.name}</p>
                    <p className="text-sm text-green-700">
                      {status.channel?.video_count} videos available
                    </p>
                  </div>
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                    Connected
                  </span>
                </div>

                {status.token_expired && (
                  <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      Your YouTube connection has expired. Please reconnect to continue importing videos.
                    </p>
                  </div>
                )}

                <div className="mt-4 flex gap-3">
                  <button
                    onClick={handleConnectYouTube}
                    className="btn-secondary text-sm"
                    disabled={isConnecting}
                  >
                    {status.token_expired ? 'Reconnect' : 'Refresh Connection'}
                  </button>
                  <button
                    onClick={handleDisconnect}
                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                    disabled={disconnectMutation.isPending}
                  >
                    {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <button
                  onClick={handleConnectYouTube}
                  disabled={isConnecting}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  {isConnecting ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                      Connect YouTube Channel
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  You'll be redirected to Google to authorize access to your YouTube channel.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Other integrations placeholder */}
      <div className="card p-6 opacity-60">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Vimeo</h3>
            <p className="text-sm text-gray-500 mt-1">
              Connect your Vimeo account to import videos and captions.
            </p>
            <button className="btn-secondary mt-4 text-sm" disabled>
              Coming Soon
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BillingSettings({ org }: { org?: OrganizationSettings }) {
  const planFeatures = {
    free: ['Up to 10 videos', '1 GB storage', 'Basic search', 'Community support'],
    starter: ['Up to 100 videos', '10 GB storage', 'Full-text search', 'Email support', '1 widget'],
    pro: ['Up to 1,000 videos', '100 GB storage', 'Advanced search', 'Priority support', 'Unlimited widgets', 'Custom branding'],
    enterprise: ['Unlimited videos', 'Unlimited storage', 'Dedicated support', 'SLA guarantees', 'Custom integrations'],
  };

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Plan</h3>
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
            <span className="text-primary-600 font-bold capitalize">{org?.plan?.[0]}</span>
          </div>
          <div>
            <p className="font-semibold text-gray-900 capitalize">{org?.plan} Plan</p>
            <p className="text-sm text-gray-500">
              {org?.max_videos} videos, {org?.max_storage_gb} GB storage
            </p>
          </div>
        </div>

        <ul className="mt-4 space-y-2">
          {planFeatures[org?.plan as keyof typeof planFeatures]?.map((feature, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
              <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
              </svg>
              {feature}
            </li>
          ))}
        </ul>

        <button className="btn-primary mt-6">Upgrade Plan</button>
      </div>

      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
        <p className="text-sm text-gray-500">No payment method on file.</p>
        <button className="btn-secondary mt-4">Add Payment Method</button>
      </div>
    </div>
  );
}

interface McpKey {
  id: string;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

interface NewMcpKey {
  id: string;
  name: string;
  key: string;
  key_prefix: string;
  created_at: string;
  mcp_url: string;
}

interface OAuthGrant {
  client_id: string;
  client_name: string;
  client_uri: string | null;
  logo_uri: string | null;
  granted_at: string;
  last_used_at: string | null;
  active_token_count: number;
}

function AiAccessSettings() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [justCreated, setJustCreated] = useState<NewMcpKey | null>(null);
  const [copyHint, setCopyHint] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['mcp-keys'],
    queryFn: () => api.get<{ data: McpKey[] }>('/mcp-keys'),
  });

  const { data: grants } = useQuery({
    queryKey: ['oauth-grants'],
    queryFn: () => api.get<{ data: OAuthGrant[] }>('/mcp-keys/oauth-grants'),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => api.post<{ data: NewMcpKey }>('/mcp-keys', { name }),
    onSuccess: (res) => {
      setJustCreated(res.data);
      setShowCreate(false);
      setNewName('');
      queryClient.invalidateQueries({ queryKey: ['mcp-keys'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/mcp-keys/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mcp-keys'] }),
  });

  const revokeGrantMutation = useMutation({
    mutationFn: (clientId: string) => api.delete(`/mcp-keys/oauth-grants/${clientId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['oauth-grants'] }),
  });

  const copy = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyHint(label);
      setTimeout(() => setCopyHint(null), 1500);
    } catch {
      // ignore
    }
  };

  const mcpUrl =
    justCreated?.mcp_url ||
    (window.location.host.includes('localhost')
      ? 'http://localhost:8787/v1/mcp'
      : 'https://api.searchmycontent.com/v1/mcp');

  const claudeDesktopConfig = (key: string) =>
    JSON.stringify(
      {
        mcpServers: {
          searchmycontent: {
            url: mcpUrl,
            headers: {
              Authorization: `Bearer ${key}`,
            },
          },
        },
      },
      null,
      2
    );

  const claudeCodeCommand = (key: string) =>
    `claude mcp add --transport http searchmycontent ${mcpUrl} --header "Authorization: Bearer ${key}"`;

  const keys = data?.data || [];
  const activeKeys = keys.filter((k) => !k.revoked_at);
  const revokedKeys = keys.filter((k) => k.revoked_at);

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">AI Access (MCP)</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-2xl">
              Connect Claude Desktop, Claude Code, Cursor, or any other Model
              Context Protocol-compatible client to your transcripts. Each API
              key grants read-only access to your videos and search.
            </p>
          </div>
          {!justCreated && (
            <button onClick={() => setShowCreate(true)} className="btn-primary whitespace-nowrap">
              Generate API Key
            </button>
          )}
        </div>

        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm">
          <span className="text-gray-600">MCP endpoint:</span>{' '}
          <code className="font-mono text-gray-900">{mcpUrl}</code>
          <button
            onClick={() => copy('url', mcpUrl)}
            className="ml-2 text-primary-600 hover:text-primary-700 text-xs"
          >
            {copyHint === 'url' ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {justCreated && (
        <div className="card p-6 border-2 border-amber-300 bg-amber-50">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-amber-900">Copy your key now</h4>
              <p className="text-sm text-amber-800 mt-1">
                This is the only time the full key will be shown. Store it
                somewhere safe — if you lose it, you'll need to generate a new
                one.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-amber-900 uppercase tracking-wider">API Key</label>
              <div className="mt-1 flex gap-2 items-center">
                <code className="flex-1 px-3 py-2 bg-white border border-amber-300 rounded font-mono text-sm break-all">
                  {justCreated.key}
                </code>
                <button onClick={() => copy('key', justCreated.key)} className="btn-secondary whitespace-nowrap">
                  {copyHint === 'key' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-amber-900 uppercase tracking-wider">
                Claude Desktop config (claude_desktop_config.json)
              </label>
              <div className="mt-1 flex gap-2 items-start">
                <pre className="flex-1 px-3 py-2 bg-white border border-amber-300 rounded font-mono text-xs overflow-x-auto">
{claudeDesktopConfig(justCreated.key)}
                </pre>
                <button
                  onClick={() => copy('claude-desktop', claudeDesktopConfig(justCreated.key))}
                  className="btn-secondary whitespace-nowrap"
                >
                  {copyHint === 'claude-desktop' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-amber-900 uppercase tracking-wider">
                Claude Code CLI
              </label>
              <div className="mt-1 flex gap-2 items-start">
                <code className="flex-1 px-3 py-2 bg-white border border-amber-300 rounded font-mono text-xs break-all">
                  {claudeCodeCommand(justCreated.key)}
                </code>
                <button
                  onClick={() => copy('claude-code', claudeCodeCommand(justCreated.key))}
                  className="btn-secondary whitespace-nowrap"
                >
                  {copyHint === 'claude-code' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <button
              onClick={() => setJustCreated(null)}
              className="btn-primary"
            >
              I've saved my key
            </button>
          </div>
        </div>
      )}

      {showCreate && !justCreated && (
        <div className="card p-6">
          <h4 className="font-semibold text-gray-900 mb-3">Generate a new key</h4>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Claude Desktop on my laptop"
            className="input mb-3"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={() => createMutation.mutate(newName)}
              disabled={!newName.trim() || createMutation.isPending}
              className="btn-primary"
            >
              {createMutation.isPending ? 'Generating...' : 'Generate Key'}
            </button>
            <button
              onClick={() => {
                setShowCreate(false);
                setNewName('');
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="font-semibold text-gray-900">Connected apps</h4>
          <p className="text-xs text-gray-500 mt-1">
            Apps that completed the OAuth flow (e.g. Claude Web). Different from
            manually-issued API keys below.
          </p>
        </div>
        {(grants?.data || []).length === 0 ? (
          <div className="text-center py-8 px-6 text-sm text-gray-500">
            No apps connected via OAuth yet. Apps appear here automatically once
            a user completes the authorization flow.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">App</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Granted</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last used</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(grants?.data || []).map((g) => (
                <tr key={g.client_id}>
                  <td className="px-6 py-3 text-sm">
                    <div className="flex items-center gap-3">
                      {g.logo_uri ? (
                        <img src={g.logo_uri} alt="" className="h-6 w-6 rounded" />
                      ) : (
                        <div className="h-6 w-6 rounded bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-500">
                          {g.client_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900">{g.client_name}</div>
                        {g.client_uri && (
                          <a
                            href={g.client_uri}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            {new URL(g.client_uri).host}
                          </a>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-500">
                    {new Date(g.granted_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-500">
                    {g.last_used_at ? new Date(g.last_used_at).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <button
                      onClick={() => {
                        if (
                          confirm(
                            `Revoke access for "${g.client_name}"? The app will lose access immediately and will need to re-authorize to connect again.`
                          )
                        ) {
                          revokeGrantMutation.mutate(g.client_id);
                        }
                      }}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="font-semibold text-gray-900">Your keys</h4>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : keys.length === 0 ? (
          <div className="text-center py-8 px-6 text-sm text-gray-500">
            No API keys yet. Generate one above to connect an AI client.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prefix</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last used</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[...activeKeys, ...revokedKeys].map((k) => (
                <tr key={k.id} className={k.revoked_at ? 'opacity-50' : ''}>
                  <td className="px-6 py-3 text-sm text-gray-900">
                    {k.name}
                    {k.revoked_at && (
                      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                        revoked
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm font-mono text-gray-500">{k.key_prefix}…</td>
                  <td className="px-6 py-3 text-sm text-gray-500">
                    {new Date(k.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-500">
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {!k.revoked_at && (
                      <button
                        onClick={() => {
                          if (confirm(`Revoke "${k.name}"? Any client using this key will lose access immediately.`)) {
                            revokeMutation.mutate(k.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card p-6 bg-gray-50">
        <h4 className="font-semibold text-gray-900 mb-2">What can AI clients do with this?</h4>
        <ul className="text-sm text-gray-600 space-y-1.5 list-disc pl-5">
          <li><code className="text-xs">search_transcripts</code> — search across every video transcript with ranked results and snippets.</li>
          <li><code className="text-xs">list_videos</code> — browse the catalog with date / status filters.</li>
          <li><code className="text-xs">get_video</code> / <code className="text-xs">get_transcript</code> — fetch metadata or the full text for a specific video.</li>
          <li><code className="text-xs">get_video_segment</code> — zoom in on a specific time window without dumping the whole transcript.</li>
          <li><code className="text-xs">search_within_video</code> — find every mention of a topic inside one video.</li>
        </ul>
        <p className="text-xs text-gray-500 mt-3">
          All access is read-only. Keys can be revoked at any time without affecting other keys.
        </p>
      </div>
    </div>
  );
}
